import { BlockPlaybook, Playbook, PlaybookStatus } from '@driftt/api-types'
import {
  Breadcrumbs,
  InlineEditInput,
  NavigationHeader,
} from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'
import { showTransientNotification } from '@driftt/traffic-control'
import { pluralize } from 'humanize-plus'
import { isEmpty } from 'ramda'
import React, { useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'

import { updatePlaybookName } from 'api/blocks'
import { hydrateConsumingPlaybooks } from 'hooks/useConsumingPlaybooks'
import { useLatestMajorBlockChanges } from 'hooks/useLatestMajorBlockChanges'
import { BlockEditorOnChangeProps } from 'layouts/BlockEditorLayout'
import { receivePlaybookName } from 'modules/playbook/actions'

import ArchivedPlaybooksModal from './ArchivedPlaybooksModal'
import BlockSubNav from './BlockSubNav'
import PlaybookUpdatesRequiredModal from './PlaybookUpdatesRequiredModal'
import UsedInPlaybooksModal from './UsedInPlaybooksModal'

import styles from './styles.module.css'

const BLOCK_BREADCRUMB_HEADINGS = [
  {
    title: 'Playbooks',
    link: '/playbooks',
  },
  {
    title: 'Reusable blocks',
    link: '/playbooks/blocks/list',
  },
  {
    title: 'Edit reusable block',
    link: '/',
    isActive: true,
  },
]

interface BlockEditorNavigationProps {
  publishedBlock: BlockPlaybook
  hasChanges: boolean
  stateRefresh: (updatedBlock: BlockPlaybook) => void
  consumingPlaybookData: PlaybookBlockData[]
  blockId: string
  onBlockEditorChange: (props: BlockEditorOnChangeProps) => void
}

export interface HydratedPlaybookBlockData {
  playbook: Playbook
  isOutdated: boolean
  playbookStatus: PlaybookStatus
}

export interface PlaybookBlockData {
  playbookId: number
  isOutdated: boolean
  playbookStatus: PlaybookStatus
  playbookName: string
}

const BlockEditorNavigation = ({
  publishedBlock,
  hasChanges,
  stateRefresh,
  consumingPlaybookData,
  blockId,
  onBlockEditorChange,
}: BlockEditorNavigationProps) => {
  const queryClient = useQueryClient()
  const [archivedPlaybooksModalOpen, setArchivedPlaybooksModalOpen] =
    useState(false)
  const [playbooksModalOpen, setPlaybooksModalOpen] = useState(false)
  const [showSaveSuccessNotif, setShowSaveSuccessNotif] = useState(false)
  const [
    playbookUpdatesRequiredModalOpen,
    setPlaybookUpdatesRequiredModalOpen,
  ] = useState(false)
  const dispatch = useDispatch()

  const consumingPlaybooks: HydratedPlaybookBlockData[] = useMemo(
    () => hydrateConsumingPlaybooks(consumingPlaybookData),
    [consumingPlaybookData]
  )

  const nonArchivedConsumingPlaybookData = consumingPlaybookData.filter(
    (pbData) => pbData.playbookStatus !== PlaybookStatus.ARCHIVED
  )

  const archivedConsumingPlaybookData = consumingPlaybookData.filter(
    (pbData) => pbData.playbookStatus === PlaybookStatus.ARCHIVED
  )

  const outdatedPlaybookData = consumingPlaybookData.filter(
    (playbookData: PlaybookBlockData) => playbookData.isOutdated
  )

  const nonArchivedOutdatedPlaybookData = outdatedPlaybookData.filter(
    (playbookData) => playbookData.playbookStatus !== PlaybookStatus.ARCHIVED
  )

  const consumingNonArchivedPlaybooksUpToDate = isEmpty(
    nonArchivedOutdatedPlaybookData
  )

  const majorVersionBlockChanges = useLatestMajorBlockChanges(blockId)

  const persistBlockName = (name: string) => {
    updatePlaybookName(blockId, name)
      .then(() => {
        dispatch(receivePlaybookName(name))
        dispatch(
          showTransientNotification({
            type: 'success',
            message: 'Block name successfully saved.',
          })
        )
      })
      .catch((err) => {
        console.warn(err)
        dispatch(
          showTransientNotification({
            type: 'error',
            message: 'There was an error saving your block name',
          })
        )
      })
  }

  const toggleArchivedPlaybooksModal = () => {
    setArchivedPlaybooksModalOpen(!archivedPlaybooksModalOpen)
  }

  const togglePlaybooksModal = () => {
    setPlaybooksModalOpen(!playbooksModalOpen)
  }

  const togglePlaybookUpdatesRequiredModal = () => {
    setPlaybookUpdatesRequiredModalOpen(!playbookUpdatesRequiredModalOpen)
  }

  const showPlaybookUpdatesRequiredModalOnSave = () => {
    queryClient.invalidateQueries(`latest-major-block-changes--${blockId}`)
    queryClient.invalidateQueries(`reusable-block--${blockId}--playbooks`)
    togglePlaybookUpdatesRequiredModal()
    setShowSaveSuccessNotif(true)
  }

  return (
    <>
      <NavigationHeader
        secondary={<Breadcrumbs headings={BLOCK_BREADCRUMB_HEADINGS} />}
        title={
          <div className={styles.blockEditorNavigation}>
            <InlineEditInput
              /* @ts-ignore  BV2 TODO: remove */
              className="playbook-name-input"
              headingSize="h3"
              onSubmit={persistBlockName}
              type="heading"
              toolTipPlacement="down"
              value={publishedBlock?.name || 'My new block'}
              shouldValidate
              withSave
            />
            {consumingPlaybooks.length <= 0 && (
              <div
                className={styles.blockEditorNavigationUsedInPlaybooksModalLink}
                onClick={togglePlaybooksModal}
              >
                Used in 0{' '}
                {pluralize(nonArchivedConsumingPlaybookData.length, 'playbook')}
              </div>
            )}
            {nonArchivedConsumingPlaybookData.length <= 0 &&
              archivedConsumingPlaybookData.length > 0 && (
                <div
                  className={
                    styles.blockEditorNavigationUsedInPlaybooksModalLink
                  }
                  onClick={toggleArchivedPlaybooksModal}
                >
                  Used in {archivedConsumingPlaybookData.length} archived{' '}
                  {pluralize(archivedConsumingPlaybookData.length, 'playbook')}
                </div>
              )}

            {nonArchivedConsumingPlaybookData.length > 0 &&
              consumingNonArchivedPlaybooksUpToDate && (
                <div
                  className={
                    styles.blockEditorNavigationUsedInPlaybooksModalLink
                  }
                  onClick={togglePlaybooksModal}
                >
                  Used in {nonArchivedConsumingPlaybookData.length}{' '}
                  {pluralize(
                    nonArchivedConsumingPlaybookData.length,
                    'playbook'
                  )}
                </div>
              )}

            {nonArchivedConsumingPlaybookData.length > 0 &&
              !consumingNonArchivedPlaybooksUpToDate && (
                <div
                  className={
                    styles.blockEditorNavigationUpdatesRequiredModalLink
                  }
                  onClick={togglePlaybookUpdatesRequiredModal}
                >
                  <Icon name="warning-triangle" />
                  Updates required in {
                    nonArchivedOutdatedPlaybookData.length
                  }{' '}
                  {pluralize(
                    nonArchivedOutdatedPlaybookData.length,
                    'playbook'
                  )}
                </div>
              )}
          </div>
        }
      ></NavigationHeader>
      {publishedBlock && (
        <BlockSubNav
          hasChanges={hasChanges}
          stateRefresh={stateRefresh}
          block={publishedBlock}
          onBlockMajorVersionUpdate={showPlaybookUpdatesRequiredModalOnSave}
          numConsumingPlaybooks={nonArchivedConsumingPlaybookData?.length}
          numOutdatedPlaybooks={nonArchivedOutdatedPlaybookData?.length}
        />
      )}
      <ArchivedPlaybooksModal
        isOpen={archivedPlaybooksModalOpen}
        onClose={toggleArchivedPlaybooksModal}
        consumingPlaybooks={consumingPlaybooks}
      />
      <UsedInPlaybooksModal
        isOpen={playbooksModalOpen}
        onClose={togglePlaybooksModal}
        consumingPlaybooks={consumingPlaybooks}
      />
      <PlaybookUpdatesRequiredModal
        showSaveNotif={showSaveSuccessNotif}
        isOpen={playbookUpdatesRequiredModalOpen}
        onClose={() => {
          togglePlaybookUpdatesRequiredModal()
          setShowSaveSuccessNotif(false)
        }}
        consumingPlaybooks={consumingPlaybooks}
        majorVersionBlockChanges={majorVersionBlockChanges}
        numOutdatedPlaybooks={nonArchivedOutdatedPlaybookData?.length || 0}
      />
    </>
  )
}

export default BlockEditorNavigation
